import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import { recomputeEcoBadge } from '../lib/ecoAward';
import { notifyDocumentToSuppliers, fetchNotifiableSuppliers } from '../lib/notifyDocument';
import NotifySuppliersModal from './NotifySuppliersModal';
import ComplianceConfigFields, {
  blankComplianceFields, normaliseComplianceFields, DEFAULT_REMINDER_RULES,
  autoApprovalRecommended,
} from './ComplianceConfigFields';

// Admin Upload-document modal — the flow for Fusion 2K to ISSUE a document
// (distinct from the supplier-submission Review Queue). Replaces the old
// "Upload flow coming soon" placeholder on Compliance → Fusion Documents.
//
// Categories are data-driven (document_categories) and carry a BEHAVIOUR model
// that drives the Visibility section:
//   shown    — a "Show to suppliers" toggle; eligible for a recognition award.
//   required — becomes a required compliance doc; which suppliers see it is set
//              by risk tier in Risk Flag Config (link out). No award. Plus a
//              "what suppliers must do" choice (upload vs acknowledge).
//   internal — visibility locked off; never shown to suppliers. No award.
//
// DRAFT → PUBLISH: new docs are created as draft (no supplier-facing effect).
// Publish flips status to active so downstream effects fire — a required doc
// appears as Outstanding (via its compliance_doc_key + Risk Flag Config); an
// eco-linked doc raises eco required and triggers the action-needed hold.

const BUCKET = 'supplier-uploads';
const PATH_PREFIX = '__fusion-documents';
const MAX_BYTES = 20 * 1024 * 1024; // 20 MB
const ACCEPT = '.pdf,.docx,.png,.jpg,.jpeg';
const ACCEPT_RE = /\.(pdf|docx|png|jpe?g)$/i;

const slugify = (s) => (s || '')
  .toLowerCase().trim()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 60) || `doc_${Date.now()}`;

const labelCls = "font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-[#51534A] block mb-1.5";
const inputCls = "w-full bg-white border border-line rounded-lg text-ink px-3 py-2 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors";

export default function UploadDocumentModal({
  open, onClose, onSaved, categories = [], badgeCategories = [], existingDocs = [], currentUser, onOpenRiskFlagConfig,
}) {
  const navigate = useNavigate();
  const [categoryKey, setCategoryKey] = useState(categories[0]?.key || '');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [versionMode, setVersionMode] = useState('new'); // 'new' | 'version'
  const [supersedeId, setSupersedeId] = useState('');
  const [showToSuppliers, setShowToSuppliers] = useState(true); // shown-category toggle
  const [satisfiedBy, setSatisfiedBy] = useState('upload'); // required-category: upload | acknowledge
  // Full compliance config (required categories only) — validity, expiry,
  // accepted types, enforcement + reminder rules. Shared with the edit modal.
  const [complianceFields, setComplianceFields] = useState(blankComplianceFields());
  const [reminderRules, setReminderRules] = useState(DEFAULT_REMINDER_RULES.map(r => ({ ...r })));
  // Once the author touches the approval toggle it's their choice — the method
  // preset must never overwrite a deliberate decision.
  const approvalTouchedRef = useRef(false);
  const setComplianceField = (key, value) => {
    if (key === 'approval_mode') approvalTouchedRef.current = true;
    setComplianceFields(prev => ({ ...prev, [key]: value }));
  };
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // 'draft' | 'publish' | 'notify' | 'test'
  const [notifyPicker, setNotifyPicker] = useState(null); // { suppliers } when the picker is open
  const [confirmWordFile, setConfirmWordFile] = useState(null); // pending .docx awaiting confirm
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Preset auto-approval for the methods it suits (form / acknowledge /
  // pdf_sign — nothing of the supplier's own to read). It stays a toggle: this
  // only sets the starting position, and only while the author hasn't chosen.
  useEffect(() => {
    if (approvalTouchedRef.current) return;
    setComplianceFields(prev => ({
      ...prev,
      approval_mode: autoApprovalRecommended(satisfiedBy) ? 'auto' : 'manual',
    }));
  }, [satisfiedBy]);

  const category = useMemo(() => categories.find(c => c.key === categoryKey) || null, [categories, categoryKey]);
  const behaviour = category?.behaviour || 'shown';
  // Recognition award is inherited from the category (set in Manage Categories),
  // not chosen per-document. required/internal categories never carry one.
  const catBadgeCategoryId = category?.badge_category_id || null;
  const catBadgeTier = category?.badge_tier_awarded || null;
  const ecoCat = useMemo(() => badgeCategories.find(b => b.slug === 'eco') || null, [badgeCategories]);
  // Notify is only meaningful for suppliers who can see the doc — 'shown' docs
  // are visible to all; 'required' publish via Risk Flag Config; 'internal' hide.
  const canNotify = behaviour === 'shown';
  const canPublish = behaviour !== 'required';
  // The METHOD (now available on every category) drives whether an admin file
  // is attached here: 'download_return' attaches our template, and a plain
  // 'upload' on a non-required (shown/internal) category is the file Fusion
  // issues. 'form' is completed in the hosted-form builder and 'acknowledge'
  // authors its content in Configure — neither needs a file. A 'required'
  // 'upload' takes the supplier's OWN file, so none is attached here.
  // 'pdf_sign' also needs a file — the source PDF we place fields on.
  const showFileZone = satisfiedBy === 'download_return' || satisfiedBy === 'pdf_sign'
    || (behaviour !== 'required' && satisfiedBy === 'upload');
  const needsFile = showFileZone;
  // Restrict the picker to PDF for pdf_sign (fields are placed on a real PDF).
  const fileAccept = satisfiedBy === 'pdf_sign' ? '.pdf' : ACCEPT;
  const supersedeOptions = useMemo(
    () => existingDocs.filter(d => d.document_type === categoryKey && d.status === 'active'),
    [existingDocs, categoryKey]
  );

  if (!open) return null;

  const acceptFile = (f) => { setError(null); setFile(f); };
  const pickFile = (f) => {
    if (!f) return;
    // pdf_sign places fields on a real PDF — accept PDF only.
    if (satisfiedBy === 'pdf_sign') {
      if (!/\.pdf(\?|$)/i.test(f.name)) { setError('Upload a PDF — fields are placed on the PDF itself.'); return; }
      if (f.size > MAX_BYTES) { setError('File is over the 20 MB limit.'); return; }
      acceptFile(f);
      return;
    }
    if (!ACCEPT_RE.test(f.name)) { setError('Accepted types: PDF, DOCX, PNG, JPG.'); return; }
    if (f.size > MAX_BYTES) { setError('File is over the 20 MB limit.'); return; }
    // Word docs are editable by suppliers — warn before accepting one.
    if (/\.docx$/i.test(f.name)) { setConfirmWordFile(f); return; }
    acceptFile(f);
  };

  const reset = () => {
    setCategoryKey(categories[0]?.key || ''); setTitle(''); setFile(null);
    setVersionMode('new'); setSupersedeId(''); setShowToSuppliers(true); setSatisfiedBy('upload');
    setComplianceFields(blankComplianceFields()); setReminderRules(DEFAULT_REMINDER_RULES.map(r => ({ ...r })));
    setError(null); setBusy(null); setNotifyPicker(null); setConfirmWordFile(null);
  };
  const openNotifyPicker = async () => {
    const suppliers = await fetchNotifiableSuppliers();
    setNotifyPicker({ suppliers });
  };
  const close = () => { reset(); onClose?.(); };

  const save = async (mode, { supplierIds = null } = {}) => {
    if (busy) return;
    if (!categoryKey) { setError('Choose a category.'); return; }
    if (!title.trim()) { setError('Give the document a title.'); return; }
    // Whether an admin file is required depends on the method (see showFileZone):
    // only 'download_return' and non-required (shown/internal) docs attach one.
    const isRequired = behaviour === 'required';
    if (needsFile && !file) {
      setError(satisfiedBy === 'download_return'
        ? 'Attach the file suppliers download, sign and return.'
        : 'Add a file.');
      return;
    }
    if (versionMode === 'version' && !supersedeId) { setError('Pick the document this replaces.'); return; }
    setBusy(mode);
    setError(null);
    try {
      // 1. Upload the file (if one was provided).
      let fileUrl = null;
      if (file) {
        const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${PATH_PREFIX}/${slugify(title)}-${Date.now()}-${safe}`;
        const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type, upsert: false });
        if (upErr) throw new Error(upErr.message);
        const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
        fileUrl = pub?.publicUrl || null;
      }

      // 'draft' → draft; 'publish'/'notify'/'test' → active. 'notify' emails all
      // suppliers after publish; 'test' emails only the acting admin (preview).
      const status = (mode === 'publish' || mode === 'notify' || mode === 'test') ? 'active' : 'draft';
      // Required docs are tracked per-supplier by a compliance_doc_key; reuse
      // the superseded doc's key on a new version so history + requirements
      // stay attached, otherwise mint one from the title.
      const supersedeDoc = supersedeOptions.find(d => d.id === supersedeId) || null;
      // A compliance_doc_key anchors per-supplier submissions + requirements.
      // Required docs always get one; and now ANY category whose method needs a
      // supplier submission (form / acknowledge / download & return) gets one
      // too, so those submissions can be tracked. A plain 'upload' library file
      // on a shown/internal category doesn't need one.
      const needsComplianceKey = isRequired
        || satisfiedBy === 'form' || satisfiedBy === 'acknowledge' || satisfiedBy === 'download_return' || satisfiedBy === 'pdf_sign';
      const complianceKey = needsComplianceKey
        ? (supersedeDoc?.compliance_doc_key || slugify(title))
        : null;

      const row = {
        document_name: title.trim(),
        document_type: categoryKey,
        status,
        // Required (risk-flag) docs are draft-only here but must surface in Risk
        // Flag Configuration so an admin can tick them for a tier — which is what
        // publishes them to that tier's suppliers. is_active shows the row in the
        // matrix; supplier visibility comes from the tier ticks, not this flag.
        is_active: status === 'active' || isRequired,
        file_url: fileUrl,
        version_value: supersedeDoc ? ((Number(supersedeDoc.version_value) || 1) + 1) : 1,
        version_year: new Date().getFullYear(),
        parent_document_id: supersedeDoc ? supersedeDoc.id : null,
        compliance_doc_key: complianceKey,
        // Visibility by behaviour: shown → the toggle; required (risk-tier) and
        // internal are never portal-visible via this flag.
        visible_in_portal: behaviour === 'shown' ? !!showToSuppliers : false,
        // What suppliers must do — now chosen on every category.
        satisfied_by: satisfiedBy,
        // pdf_sign: the uploaded PDF is the immutable SOURCE we place fields on.
        // field_layout is authored next in the field-placement tool.
        ...(satisfiedBy === 'pdf_sign' ? { source_pdf_path: fileUrl } : {}),
        // Acknowledge requires lifecycle + source (DB CHECK
        // fusion_documents_acknowledge_required_fields). The content suppliers
        // sign is authored later in Configure, so seed sensible defaults here.
        ...(satisfiedBy === 'acknowledge'
          ? { lifecycle: 'one_off', source: 'richtext' }
          : {}),
        // Document config — validity/expiry, accepted file types, enforcement —
        // from ComplianceConfigFields, now available on EVERY category (not just
        // compliance). Its blankComplianceFields() defaults keep non-expiring
        // docs sensible when the author leaves the fields untouched.
        ...normaliseComplianceFields(complianceFields),
        // Recognition award — inherited from the category (Manage Categories),
        // not chosen per-document. The category keeps the pair co-present, so
        // stamping both satisfies fusion_documents_badge_pair_check. Eco
        // progression is driven by the category link alone (oak stages, see
        // ecoAward); the stored tier is inert for eco.
        badge_category_id: catBadgeCategoryId,
        badge_tier_awarded: catBadgeTier,
        uploaded_by: currentUser?.id || null,
      };

      const { data: inserted, error: insErr } = await supabase.from('fusion_documents').insert([row]).select('id').single();
      if (insErr) throw new Error(insErr.message);

      // On PUBLISH, a new version supersedes the previous active one.
      if (status === 'active' && supersedeDoc) {
        await supabase.from('fusion_documents')
          .update({ status: 'superseded', is_active: false, superseded_at: new Date().toISOString(), superseded_by_id: inserted.id })
          .eq('id', supersedeDoc.id);
      }

      // Auto-reminder rules — keyed by compliance_doc_key. Any document with a
      // key (compliance, or a form/acknowledge/download-return on any category)
      // gets them. Wipe + reinsert, mirroring the old Risk Flag drawer save.
      if (complianceKey) {
        // Insert the new rules BEFORE removing the old ones. Delete-first with
        // an unchecked insert leaves the document with NO reminder rules, and
        // nothing ever announces that: the symptom is reminder emails silently
        // not going out, which nobody notices until a document expires
        // unchased.
        const { data: priorRules } = await supabase
          .from('document_reminder_rules').select('id').eq('document_key', complianceKey);
        const priorRuleIds = (priorRules || []).map(r => r.id);
        let rulesFailed = null;
        if (reminderRules.length > 0) {
          const { error: rulesErr } = await supabase.from('document_reminder_rules').insert(reminderRules.map(r => ({
            document_key: complianceKey,
            timing_value: r.timing_value || 0,
            timing_unit: r.timing_unit || 'days',
            timing_direction: r.timing_direction,
            cadence: r.cadence || 'one_time',
            created_by: currentUser?.id || null,
          })));
          rulesFailed = rulesErr || null;
        }
        if (rulesFailed) {
          setError(`The document saved, but its reminder rules didn't: ${rulesFailed.message}. The previous rules are still in place.`);
        } else if (priorRuleIds.length > 0) {
          await supabase.from('document_reminder_rules').delete().in('id', priorRuleIds);
        }
      }

      // Eco ripple on publish: a newly-published eco-linked doc raises some
      // suppliers' required eco count. Recompute their oak so the action-needed
      // HOLD fires (no visible step-back) per the Eco addendum. Conservative:
      // recompute active suppliers; recomputeEcoBadge no-ops where eco isn't
      // applicable. (Risk-tier applicability stays owned by Risk Flag Config.)
      if (status === 'active' && ecoCat && catBadgeCategoryId === ecoCat.id) {
        const { data: sups } = await supabase.from('suppliers').select('id, risk_flag').eq('status', 'active');
        for (const s of (sups || [])) {
          try { await recomputeEcoBadge(supabase, s.id, s.risk_flag || null); } catch { /* skip */ }
        }
      }

      // Notify & publish (shown docs only): email every active supplier's portal
      // users that a new document is available. 'test' emails only the acting
      // admin so the email can be previewed first. Best-effort — a publish
      // already succeeded, so a mail hiccup shouldn't roll it back.
      let notifyResult = null;
      if ((mode === 'notify' || mode === 'test') && status === 'active') {
        try {
          notifyResult = await notifyDocumentToSuppliers({
            documentLabel: title.trim(),
            sentByUserId: currentUser?.id || null,
            testEmail: mode === 'test'
              ? (currentUser?.email || 'robynne.elliott@fusion-events.com')
              : null,
            supplierIds: mode === 'notify' ? supplierIds : null,
          });
        } catch { /* surfaced as a plain publish below */ }
      }

      // Hosted-form docs (any category) are authored, not attached: created as a
      // draft here, then routed straight to the prose editor to build content.
      if (satisfiedBy === 'form') {
        reset();
        onClose?.();
        navigate(`/documents/${inserted.id}/build`);
        return;
      }
      // pdf_sign: the PDF is uploaded; place the fields on it next.
      if (satisfiedBy === 'pdf_sign') {
        reset();
        onClose?.();
        navigate(`/documents/${inserted.id}/pdf-fields`);
        return;
      }

      onSaved?.(status, notifyResult);
      close();
    } catch (e) {
      setError(e.message || 'Could not save the document.');
      setBusy(null);
    }
  };

  return (
    <Modal open onClose={close} maxWidth={680} ariaLabel="New document">
      <Modal.Header eyebrow="Fusion Documents" title="New document" onClose={close} />
      <Modal.Body>
        {/* Category */}
        <div className="mb-5">
          <label className={labelCls}>Category</label>
          <div className="flex flex-wrap gap-2">
            {categories.map(c => (
              <button
                key={c.key}
                type="button"
                onClick={() => setCategoryKey(c.key)}
                className={`font-['Metropolis'] text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${
                  categoryKey === c.key
                    ? 'border-[#92D400] text-fusion-forest bg-[#92D400]/10'
                    : 'border-line text-fusion-mid-grey hover:border-[#DADCCD]'
                }`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Method — how the supplier satisfies this document. Available on EVERY
            category now (not just compliance). Drives whether a file is attached
            here + what happens on the supplier side. Sits right after Category
            because it reshapes the sections below. */}
        <div className="mb-5">
          <label className={labelCls}>How suppliers satisfy this</label>
          <select className={inputCls} value={satisfiedBy} onChange={e => setSatisfiedBy(e.target.value)}>
            <option value="upload">{behaviour === 'required' ? 'Upload their own copy — e.g. insurance certificate' : 'A file — Fusion attaches the document to show'}</option>
            <option value="download_return">Download &amp; return — they sign our file offline and re-upload it</option>
            <option value="form">Complete a hosted form — filled in and signed in-app</option>
            <option value="acknowledge">Acknowledge / sign — they read and sign off, no fields</option>
            <option value="pdf_sign">Sign a PDF — place fields on an existing PDF, they sign it in the portal</option>
          </select>
          {satisfiedBy === 'form' && (
            <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1.5 leading-relaxed">
              Save this as a draft, then build the form’s questions in the form builder. It stays a draft until the form is filled in.
            </p>
          )}
          {satisfiedBy === 'pdf_sign' && (
            <div className="mt-1.5 rounded-lg bg-[#EEF1E6] border border-line px-3 py-2.5">
              <p className="font-['Metropolis'] text-[11px] text-[#51534A] leading-relaxed">
                <strong className="text-ink">Best for a document that already exists</strong> — legally settled, or from a third party. No rework.
              </p>
              <p className="font-['Metropolis'] text-[11px] text-[#7A4E10] leading-relaxed mt-1">
                A PDF can’t carry plain-English clause summaries or per-clause ticks, is a fixed width, and its accessibility depends on the source. Where those matter, use a <strong>hosted form</strong> instead.
              </p>
            </div>
          )}
        </div>

        {/* Title */}
        <div className="mb-5">
          <label className={labelCls}>Document title</label>
          <input className={inputCls} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Public Liability Insurance" />
        </div>

        {/* File — only where the admin attaches one: shown/internal issue a file,
            and 'download & return' attaches the template suppliers sign. 'upload',
            'form' and 'acknowledge' need no file here, so the zone is hidden. */}
        {showFileZone && (
          <div className="mb-5">
            <label className={labelCls}>
              {satisfiedBy === 'pdf_sign' ? 'PDF to place fields on' : satisfiedBy === 'download_return' ? 'File suppliers download & return' : 'File'}
            </label>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
              onClick={() => fileInputRef.current?.click()}
              className={`rounded-lg border border-dashed px-4 py-6 text-center cursor-pointer transition-colors ${dragOver ? 'border-[#92D400] bg-[#92D400]/[0.04]' : 'border-line hover:border-[#DADCCD]'}`}>
              <input ref={fileInputRef} type="file" accept={fileAccept} className="hidden" onChange={e => pickFile(e.target.files?.[0])} />
              {file ? (
                <p className="font-['Metropolis'] text-sm text-ink">{file.name}</p>
              ) : (
                <p className="font-['Metropolis'] text-xs text-[#51534A]">Drag a file here, or click to browse · {satisfiedBy === 'pdf_sign' ? 'PDF only' : 'PDF, DOCX, PNG, JPG'} · up to 20 MB</p>
              )}
            </div>
            {satisfiedBy === 'download_return' && (
              <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1.5 leading-relaxed">
                Suppliers download this file, sign it offline and re-upload their signed copy.
              </p>
            )}
          </div>
        )}

        {/* Version */}
        <div className="mb-5">
          <label className={labelCls}>Version</label>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer font-['Metropolis'] text-sm text-fusion-mid-grey">
              <input type="radio" name="ver" checked={versionMode === 'new'} onChange={() => setVersionMode('new')} className="accent-[#92D400]" />
              New document
            </label>
            <label className="flex items-center gap-2 cursor-pointer font-['Metropolis'] text-sm text-fusion-mid-grey">
              <input type="radio" name="ver" checked={versionMode === 'version'} onChange={() => setVersionMode('version')} className="accent-[#92D400]" />
              New version of an existing document
            </label>
            {versionMode === 'version' && (
              <select className={`${inputCls} mt-1`} value={supersedeId} onChange={e => setSupersedeId(e.target.value)}>
                <option value="">Select the document this replaces…</option>
                {supersedeOptions.map(d => <option key={d.id} value={d.id}>{d.document_name}</option>)}
              </select>
            )}
            {versionMode === 'version' && (
              <p className="font-['Metropolis'] text-[11px] text-[#51534A]">Version history is kept; suppliers who actioned the old version may be asked to re-acknowledge.</p>
            )}
          </div>
        </div>

        {/* Visibility — adapts to the category behaviour. */}
        <div className="mb-5">
          <label className={labelCls}>Visibility</label>
          {behaviour === 'shown' && (
            <label className="flex items-center gap-2 cursor-pointer font-['Metropolis'] text-sm text-fusion-mid-grey">
              <input type="checkbox" checked={showToSuppliers} onChange={e => setShowToSuppliers(e.target.checked)} className="accent-[#92D400]" />
              Show to suppliers
            </label>
          )}
          {behaviour === 'required' && (
            <div className="font-['Metropolis'] text-[12px] text-fusion-mid-grey leading-relaxed">
              <p>This becomes a <strong className="text-ink">required compliance document</strong>. Which suppliers see it is set by risk tier in{' '}
                <button type="button" onClick={() => onOpenRiskFlagConfig?.()} className="text-[#2E8FA1] hover:underline bg-transparent border-0 p-0 cursor-pointer">Risk Flag Configuration →</button>
              </p>
              <p className="text-[11px] text-[#51534A] mt-1.5">Once published it shows as Outstanding until {
                satisfiedBy === 'acknowledge' ? 'acknowledged'
                  : satisfiedBy === 'form' ? 'the form is completed and approved'
                  : satisfiedBy === 'download_return' ? 'the signed copy is returned and approved'
                  : 'uploaded and approved'
              }; it counts towards green compliance.</p>
            </div>
          )}
          {behaviour === 'internal' && (
            <p className="font-['Metropolis'] text-[12px] text-[#51534A]">Internal documents are never shown to suppliers — visibility is locked off.</p>
          )}
        </div>

        {/* Document config — validity, expiry, accepted file types, reminder
            rules, enforcement. Available on EVERY category now. */}
        {(
          <div className="mb-5 border-t border-line pt-5">
            <ComplianceConfigFields
              fields={complianceFields}
              setField={setComplianceField}
              rules={reminderRules}
              setRules={setReminderRules}
              satisfiedBy={satisfiedBy} />
          </div>
        )}

        {/* Recognition award — inherited from the category (set in Manage
            Categories). Shown here read-only so the uploader knows what this
            document will confer. */}
        {catBadgeCategoryId && (
          <div className="mb-2">
            <label className={labelCls}>Recognition award</label>
            <p className="font-['Metropolis'] text-[12px] text-fusion-mid-grey">
              Inherited from this category: <strong className="text-ink">{badgeCategories.find(b => b.id === catBadgeCategoryId)?.name || 'Award'}{badgeCategories.find(b => b.id === catBadgeCategoryId)?.slug === 'eco' ? ' (Acorn → Oak Status)' : ''}</strong>.
              {' '}Change it in Manage Categories to re-link every document here.
            </p>
          </div>
        )}

        {error && <p className="font-['Metropolis'] text-sm text-[#E0290A] mt-3">{error}</p>}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {/* Required (risk-flag) docs can't be published here — they go live
              per risk tier in Risk Flag Configuration. Save as draft only. */}
          <p className="font-['Metropolis'] text-[11px] text-[#51534A] leading-snug max-w-[300px]">
            {behaviour === 'required'
              ? 'Compliance documents go live per risk tier in Risk Flag Configuration — save it as a draft here first.'
              : ''}
          </p>
          <div className="flex items-center justify-end gap-3 ml-auto">
            <button type="button" onClick={close} disabled={!!busy}
              className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 cursor-pointer disabled:opacity-50">Cancel</button>
            <button type="button" onClick={() => save('draft')} disabled={!!busy}
              className="bg-white border border-line rounded-xl text-fusion-mid-grey font-['Metropolis'] text-xs font-semibold px-4 py-2 hover:border-[#DADCCD] transition-colors disabled:opacity-50">
              {busy === 'draft' ? 'Saving…' : 'Save as draft'}
            </button>
            {canPublish && (
              <button type="button" onClick={() => save('publish')} disabled={!!busy}
                className="bg-white border border-[#92D400] text-fusion-forest rounded-xl font-['Metropolis'] text-xs font-bold px-4 py-2 hover:bg-[#92D400]/10 transition-colors disabled:opacity-50">
                {busy === 'publish' ? 'Publishing…' : 'Silent publish'}
              </button>
            )}
            {canNotify && (
              <button type="button" onClick={() => save('test')} disabled={!!busy}
                title="Publishes for real, but emails the new-document notification to you only — so you can preview it before the real send."
                className="bg-white border border-line text-fusion-mid-grey rounded-xl font-['Metropolis'] text-xs font-semibold px-4 py-2 hover:border-[#DADCCD] transition-colors disabled:opacity-50">
                {busy === 'test' ? 'Sending…' : 'Test notify'}
              </button>
            )}
            {canNotify && (
              <button type="button" onClick={openNotifyPicker} disabled={!!busy}
                className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-bold px-5 py-2 hover:bg-[#a4e810] transition-colors disabled:opacity-50">
                {busy === 'notify' ? 'Publishing…' : 'Notify & publish'}
              </button>
            )}
          </div>
        </div>
      </Modal.Footer>

      <NotifySuppliersModal
        open={!!notifyPicker}
        title="Notify &amp; publish"
        intro={`Publish "${title.trim() || 'this document'}" and email the chosen suppliers' portal users that a new document is available. Each send is logged to the supplier's Activity tab.`}
        suppliers={notifyPicker?.suppliers || []}
        busy={busy === 'notify'}
        confirmLabel="Publish & notify"
        onConfirm={(ids) => { setNotifyPicker(null); save('notify', { supplierIds: ids }); }}
        onCancel={() => setNotifyPicker(null)} />

      <ConfirmDialog
        open={!!confirmWordFile}
        title="Upload a Word document?"
        body="Suppliers can edit Word (.docx) files, so anything they shouldn't change — signed terms, fixed instructions — is safer as a PDF. Upload this Word document anyway?"
        confirmLabel="Upload anyway"
        cancelLabel="Choose another file"
        onConfirm={() => { if (confirmWordFile) acceptFile(confirmWordFile); setConfirmWordFile(null); }}
        onCancel={() => setConfirmWordFile(null)} />
    </Modal>
  );
}
